import { Star, Quote, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

const GoogleIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const GoogleReviews = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation(0.2);
  const { language } = useLanguage();

  const reviews = [
    {
      name: "Edvin Ljungsten",
      rating: 5,
      date: language === 'sv' ? '3 veckor sedan' : '3 weeks ago',
      text: language === 'sv'
        ? "Extremt bra upplevelse. Personalen var exceptionell och mycket vänlig. Maten var helt otrolig och värd pengarna. Rekommenderar starkt!"
        : "This was an extremely good experience. The staff were exceptional and very kind. The food was absolutely incredible and worth the money. I highly recommend going!",
      avatar: "EL"
    },
    {
      name: "Emma Gustafsson",
      rating: 5,
      date: language === 'sv' ? '1 månad sedan' : 'a month ago',
      text: language === 'sv'
        ? "Supergod mat och trevlig personal. Rekommenderar deras sushi!"
        : "Super tasty food and friendly staff. Recommend their sushi!",
      avatar: "EG"
    },
    {
      name: "Ylva Möller",
      rating: 5,
      date: language === 'sv' ? '4 månader sedan' : '4 months ago',
      text: language === 'sv'
        ? "Bästa indiska maten på 15 år! Fantastisk Hot Chilli Chicken!!! Mycket trevlig service."
        : "Best Indian food in 15 years! Amazing Hot Chilli Chicken!!! Very nice service.",
      avatar: "YM"
    },
    {
      name: "Mia Svensson",
      rating: 5,
      date: language === 'sv' ? '8 månader sedan' : '8 months ago',
      text: language === 'sv'
        ? "Riktigt god mat och bra service. Bästa vitlöksnaan jag någonsin ätit. Kommer tillbaka!"
        : "Really good food and great service. Best garlic naan I ever had. Will come back!",
      avatar: "MS"
    },
    {
      name: "Tova Sebrén",
      rating: 5,
      date: language === 'sv' ? '1 månad sedan' : 'a month ago',
      text: language === 'sv'
        ? "Så fin upplevelse och utsökt mat!!! Rekommenderar starkt"
        : "Such a nice experience and delicious food!!! Highly recommend",
      avatar: "TS"
    },
    {
      name: "Madeleine Eliasson",
      rating: 5,
      date: language === 'sv' ? '8 månader sedan' : '8 months ago',
      text: language === 'sv'
        ? "Så god mat och magisk personal ❤️"
        : "Such good food and magical staff ❤️",
      avatar: "ME"
    }
  ];

  const averageRating = 4.3;
  const totalReviews = 111;

  const renderStars = (rating: number, size = "w-6 h-6") => {
    const fullStars = Math.floor(rating);
    const partialFill = Math.round((rating - fullStars) * 100);
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => {
          if (i < fullStars) {
            return <Star key={i} className={`${size} fill-amber-400 text-amber-400`} />;
          }
          if (i === fullStars && partialFill > 0) {
            return (
              <div key={i} className="relative">
                <Star className={`${size} text-amber-400/30`} />
                <div className="absolute inset-0 overflow-hidden" style={{ width: `${partialFill}%` }}>
                  <Star className={`${size} fill-amber-400 text-amber-400`} />
                </div>
              </div>
            );
          }
          return <Star key={i} className={`${size} text-amber-400/30`} />;
        })}
      </div>
    );
  };

  return (
    <section className="section-padding bg-card" ref={sectionRef}>
      <div className="container-custom">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-muted-foreground text-lg font-medium tracking-wider uppercase mb-4">
            {language === 'sv' ? 'RECENSIONER' : 'REVIEWS'}
          </p>
          <h2 className="heading-luxury text-foreground mb-6 text-gradient">
            {language === 'sv' ? 'Vad Våra Gäster Säger' : 'What Our Guests Say'}
          </h2>
          
          {/* Google Rating Summary */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <GoogleIcon className="w-10 h-10" />
            <div className="flex items-center gap-3">
              <span className="text-5xl font-bold text-foreground">{averageRating}</span>
              <div>
                {renderStars(averageRating)}
                <p className="text-sm text-muted-foreground mt-1">
                  {totalReviews} {language === 'sv' ? 'recensioner på Google' : 'reviews on Google'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <Card
                key={index}
                className={`group border-border bg-background transition-all duration-700 hover:shadow-[0_15px_40px_rgba(212,175,55,0.15)] hover:-translate-y-2 relative overflow-hidden before:absolute before:inset-0 before:rounded-lg before:p-[1px] before:bg-gradient-to-br before:from-transparent before:via-transparent before:to-transparent hover:before:from-primary/20 hover:before:via-transparent hover:before:to-primary/20 before:transition-all before:duration-500 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                }`}
                style={{ transitionDelay: `${0.3 + index * 0.1}s` }}
              >
                <CardContent className="p-6 relative z-10">
                  {/* Google icon + Quote */}
                  <div className="flex items-center justify-between mb-4">
                    <Quote className="w-7 h-7 text-muted-foreground/20" />
                    <GoogleIcon className="w-5 h-5 opacity-60 group-hover:opacity-100 transition-opacity" />
                  </div>
                  
                  {/* Rating */}
                  <div className="flex gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                    "{review.text}"
                  </p>

                  {/* Reviewer Info */}
                  <div className="flex items-center gap-3 pt-4 border-t border-border">
                    <div className="w-10 h-10 rounded-full bg-muted text-foreground flex items-center justify-center font-semibold text-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-primary/10">
                      {review.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{review.name}</p>
                      <p className="text-xs text-muted-foreground">{review.date}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-10 transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '0.8s' }}>
          <Button
            size="lg"
            className="bg-foreground hover:bg-foreground/90 text-background font-semibold"
            asChild
          >
            <a 
              href="https://g.page/r/CeeO9qhO0asuEBM/review" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              {language === 'sv' ? 'Lämna en recension på Google' : 'Leave a review on Google'}
            </a>
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            {language === 'sv' ? 'Verifierade recensioner från våra gäster' : 'Verified reviews from our guests'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
